import { BLICKWINKEL } from "@mrmachine/fach";
import type { Blickwinkel } from "@mrmachine/fach";

/* ══════════════════════════════════════════════════════════════════
   Welches Foto wozu gehört (L1, 25.09.2026) — die Regel, ohne Oberfläche.

   | woher | steht am Bild |
   |---|---|
   | Pflichtfoto („Foto vorne") | der Blickwinkel (0173) |
   | Kamera an einer Prüffrage | die Kennung der Frage (0185) |
   | Knopf „Foto" unten | nichts — es ist ein weiteres |

   Gezeichnet wird das in `bausteine/Fotogruppen.tsx`; hier steht nur, wie
   verteilt wird, damit es sich ohne Browser prüfen lässt
   (`apps/web/test/fotoverteilung.test.ts`).
   ══════════════════════════════════════════════════════════════════ */

/** So viel braucht die Verteilung von einem Bild — `Bild` aus `daten/fotos.ts` passt. */
export type Verteilbild = {
  pfad: string;
  notiz: string;
  blickwinkel: Blickwinkel | null;
  frageId: string;
};

export type Prueffrage = { id: string; text: string };

/**
 * **Gehört das Bild zu dieser Frage?** Fest über die Kennung (0185). Ein Bild
 * von davor trägt keine — dann über den Fragetext, den das Telefon seit jeher
 * als Notiz mitschickt. Das ist der Altbestand und nicht die Regel: Ein
 * umformulierter Fragetext trifft ihn nicht mehr, genau deshalb gibt es die
 * Kennung.
 */
export function gehoertZuFrage(b: Verteilbild, frage: Prueffrage): boolean {
  if (b.frageId) return b.frageId === frage.id;
  return !b.blickwinkel && b.notiz.trim() !== "" && b.notiz.trim() === frage.text.trim();
}

export type Fotoverteilung<B extends Verteilbild> = {
  jeFrage: Map<string, B[]>;
  /** Die Pflichtplätze in der Reihenfolge der Liste — auch die leeren. */
  jePlatz: { platz: Blickwinkel; bilder: B[] }[];
  weitere: B[];
};

/**
 * Verteilt die Bilder auf Frage, Pflichtplatz und „Weitere".
 *
 * `pflicht` ist die Liste des Geräts für diese Protokollart. Ein Platz, der
 * dort nicht steht, aber ein Bild hat (die Liste wurde seitdem geändert),
 * kommt dahinter — in der festen Reihenfolge der Blickwinkel. **Ein Bild
 * verschwindet nie**, nur weil sich eine Einstellung geändert hat. Was noch
 * keinen Ablagepfad hat (liegt nur auf dem Gerät), zählt nicht mit.
 */
export function fotosVerteilen<B extends Verteilbild>(
  bilder: readonly B[],
  { fragen = [], pflicht = [] }: { fragen?: readonly Prueffrage[]; pflicht?: readonly Blickwinkel[] },
): Fotoverteilung<B> {
  const jeFrage = new Map<string, B[]>();
  const jePlatz = new Map<Blickwinkel, B[]>();
  const weitere: B[] = [];
  for (const b of bilder) {
    if (!b.pfad) continue;
    if (b.blickwinkel) {
      jePlatz.set(b.blickwinkel, [...(jePlatz.get(b.blickwinkel) ?? []), b]);
      continue;
    }
    const frage = fragen.find((f) => gehoertZuFrage(b, f));
    if (frage) {
      jeFrage.set(frage.id, [...(jeFrage.get(frage.id) ?? []), b]);
      continue;
    }
    weitere.push(b);
  }
  const plaetze = [...pflicht, ...BLICKWINKEL.filter((w) => !pflicht.includes(w) && jePlatz.has(w))];
  return {
    jeFrage,
    jePlatz: plaetze.map((platz) => ({ platz, bilder: jePlatz.get(platz) ?? [] })),
    weitere,
  };
}
